import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from '../contexts/AuthContext';
import publicApi from './publicApi';
import siteTextService from '../services/siteTextService';
import { DEFAULT_TEXTS, defOf, maxLengthOf } from './content';
import { NEWS, NewsItem } from './newsData';
import { withoutEmphasis } from './emphasis';
import { directionsUrl } from './siteData';

/**
 * Die Inhalte der öffentlichen Website: ausgelieferter Stand aus `content/` und
 * `newsData.ts`, überlagert von dem, was der Kunde geändert hat.
 *
 * Der Abruf darf scheitern, ohne dass die Seite darunter leidet — dann zeigt sie
 * den ausgelieferten Stand.
 *
 * Beim Prerendering gibt es einen dritten Weg: Dort liegen die Inhalte bereits als
 * `window.__SITE_CONTENT__` vor, hineingelegt vom Bauskript. Der Browser im
 * Prerendering könnte die API nämlich gar nicht fragen — er lädt die Seite von
 * 127.0.0.1, und das wäre eine fremde Herkunft. So enthält das statische HTML
 * trotzdem den aktuellen Stand.
 */

/** Was das Bauskript der Seite vor dem Rendern hinlegt (`scripts/prerender.js`). */
interface VorgelegteInhalte {
  texts?: Record<string, string>;
  posts?: NewsItem[];
}

const vorgelegt = (): VorgelegteInhalte | null =>
  (typeof window !== 'undefined' && (window as any).__SITE_CONTENT__) || null;

/**
 * Läuft die Seite gerade im Prerendering-Browser? Dann gibt es kein Netz und
 * nichts nachzuladen. Im echten Browser liegen die Inhalte zwar auch schon vor
 * (das Bauskript schreibt sie in die Seite, damit React den vorgerenderten DOM
 * übernehmen kann), sie stammen aber vom letzten nächtlichen Bau — ein mittags
 * geänderter Text oder ein neuer Beitrag käme sonst erst am nächsten Morgen.
 */
const prerendering = (): boolean =>
  typeof window !== 'undefined' && Boolean((window as any).__PRERENDER__);

/**
 * `{telefon}` und Co. einsetzen. Unbekannte Platzhalter bleiben unverändert stehen:
 * Ein sichtbarer Tippfehler ist besser als ein stillschweigend verschluckter Satzteil.
 */
const applyPlaceholders = (text: string, values: Record<string, string>): string =>
  text.replace(/\{([a-z]+)\}/g, (match, name: string) => values[name] ?? match);

/**
 * Telefonnummer aus der angezeigten Schreibweise in eine wählbare Form bringen:
 * „05371 – 75 00 111" wird zu „+4953717500111". Führende Null heißt Inland, ein
 * bereits vorhandenes Pluszeichen bleibt unangetastet.
 */
const dialable = (label: string): string => {
  const digits = label.replace(/[^\d+]/g, '');
  return digits.startsWith('0') ? `+49${digits.slice(1)}` : digits;
};

export interface ContactData {
  company: string;
  street: string;
  zip: string;
  city: string;
  address: string;
  phoneLabel: string;
  phoneHref: string;
  whatsappLabel: string;
  whatsappHref: string;
  email: string;
  emailHref: string;
  /** Anfahrt in Google Maps — Ziel ist die aktuelle Adresse. */
  directionsHref: string;
}

export interface SiteContentValue {
  /** Anzuzeigender Text: Änderung oder Original, Platzhalter ersetzt. */
  t: (key: string) => string;
  /** Wie `t`, aber ohne `**`-Auszeichnung — für Titel, Alt-Texte, strukturierte Daten. */
  plain: (key: string) => string;
  /** Text so, wie er im Editor steht: mit Platzhaltern, ohne Ersetzung. */
  raw: (key: string) => string;
  /** Weicht die Stelle vom ausgelieferten Stand ab? */
  isChanged: (key: string) => boolean;
  /** Empfohlene Höchstlänge dieser Textstelle. */
  maxLength: (key: string) => number;
  /** Beschriftung im Editor. */
  labelOf: (key: string) => string;

  /**
   * Die veröffentlichten Beiträge. Solange der Abruf läuft oder scheitert, ist es
   * der ausgelieferte Bestand aus `newsData.ts` — eine vom Kunden geleerte Liste
   * bleibt dagegen leer und wird nicht durch den Bestand ersetzt.
   */
  news: NewsItem[];
  /** Kamen die Beiträge tatsächlich vom Server? */
  newsLoaded: boolean;

  /** Darf der angemeldete Benutzer Texte ändern? */
  canEdit: boolean;
  editing: boolean;
  setEditing: (editing: boolean) => void;
  /** Anzahl der Stellen, die vom ausgelieferten Stand abweichen. */
  changedCount: number;

  save: (key: string, value: string) => Promise<void>;
  reset: (key: string) => Promise<void>;
}

const SiteContentContext = createContext<SiteContentValue | undefined>(undefined);

const EDIT_MODE_KEY = 'website-edit-mode';

export const SiteContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [overrides, setOverrides] = useState<Record<string, string>>(() => vorgelegt()?.texts ?? {});
  const [news, setNews] = useState<NewsItem[] | null>(() => vorgelegt()?.posts ?? null);
  const [editing, setEditingState] = useState(false);

  const canEdit = isAuthenticated && isAdmin();

  useEffect(() => {
    // Beim Prerendering liegen die Inhalte schon vor; dann gibt es nichts zu holen.
    if (prerendering()) return undefined;

    let active = true;
    publicApi
      .get<{ texts: Record<string, string> }>('/site-content')
      .then((response) => {
        if (active) setOverrides(response.data.texts ?? {});
      })
      .catch(() => {
        // Ohne erreichbares Backend bleibt es beim ausgelieferten Stand.
      });

    publicApi
      .get<{ posts: NewsItem[] }>('/news')
      .then((response) => {
        if (active) setNews(response.data.posts ?? []);
      })
      .catch(() => {
        // dito — dann zeigt die Seite die ausgelieferten Beiträge.
      });

    return () => {
      active = false;
    };
  }, []);

  // Der Bearbeiten-Modus überlebt den Seitenwechsel, aber nicht den Tab:
  // Wer die Website später erneut aufruft, soll sie sehen wie ein Besucher.
  useEffect(() => {
    if (canEdit && window.sessionStorage.getItem(EDIT_MODE_KEY) === 'on') setEditingState(true);
  }, [canEdit]);

  const setEditing = useCallback((next: boolean) => {
    setEditingState(next);
    if (next) window.sessionStorage.setItem(EDIT_MODE_KEY, 'on');
    else window.sessionStorage.removeItem(EDIT_MODE_KEY);
  }, []);

  const value = useMemo<SiteContentValue>(() => {
    const original = (key: string): string => {
      const entry = DEFAULT_TEXTS[key];
      // Ein unbekannter Schlüssel ist ein Programmierfehler; sichtbar zu machen ist
      // besser als eine leere Stelle, die beim Durchsehen der Seite nicht auffällt.
      if (entry === undefined) return `[${key}]`;
      return defOf(entry).text;
    };
    const raw = (key: string): string => overrides[key] ?? original(key);

    // Die Platzhalterwerte kommen aus denselben Texten und sind deshalb selbst
    // bearbeitbar: Wer die Telefonnummer ändert, ändert sie überall dort mit, wo
    // `{telefon}` im Fließtext steht.
    const values: Record<string, string> = {
      telefon: raw('kontaktdaten.telefon'),
      whatsapp: raw('kontaktdaten.whatsapp'),
      email: raw('kontaktdaten.email'),
      adresse: `${raw('kontaktdaten.strasse')}, ${raw('kontaktdaten.plz')} ${raw('kontaktdaten.ort')}`,
      ort: raw('kontaktdaten.ort'),
      firma: raw('kontaktdaten.firma'),
      jahr: String(new Date().getFullYear()),
    };
    const t = (key: string): string => applyPlaceholders(raw(key), values);

    return {
      t,
      plain: (key) => withoutEmphasis(t(key)),
      raw,
      isChanged: (key) => overrides[key] !== undefined,
      maxLength: (key) => {
        const entry = DEFAULT_TEXTS[key];
        return entry === undefined ? 300 : maxLengthOf(entry);
      },
      labelOf: (key) => {
        const entry = DEFAULT_TEXTS[key];
        return (entry !== undefined && defOf(entry).label) || key;
      },
      news: news ?? NEWS,
      newsLoaded: news !== null,
      canEdit,
      editing: canEdit && editing,
      setEditing,
      changedCount: Object.keys(overrides).length,
      save: async (key, newValue) => {
        const saved = await siteTextService.save(key, newValue);
        setOverrides((current) => ({ ...current, [key]: saved.value }));
      },
      reset: async (key) => {
        await siteTextService.reset(key);
        setOverrides((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
      },
    };
  }, [overrides, news, canEdit, editing, setEditing]);

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
};

export const useSiteContent = (): SiteContentValue => {
  const context = useContext(SiteContentContext);
  if (!context) throw new Error('useSiteContent muss innerhalb von SiteContentProvider stehen');
  return context;
};

/**
 * Die Kontaktdaten des Betriebs, wie sie aktuell auf der Website stehen.
 *
 * Anrufen, WhatsApp und E-Mail werden aus der **angezeigten** Angabe abgeleitet:
 * Ändert der Kunde die Telefonnummer, führt der Anruf-Link auf dieselbe Nummer.
 * Nicht abgeleitet wird die Kartenposition — sie hängt an `OFFICE_COORDS` im Code.
 */
export const useContact = (): ContactData => {
  const { t } = useSiteContent();

  return useMemo(() => {
    const street = t('kontaktdaten.strasse');
    const zip = t('kontaktdaten.plz');
    const city = t('kontaktdaten.ort');
    const phoneLabel = t('kontaktdaten.telefon');
    const whatsappLabel = t('kontaktdaten.whatsapp');
    const email = t('kontaktdaten.email');

    return {
      company: t('kontaktdaten.firma'),
      street,
      zip,
      city,
      address: `${street}, ${zip} ${city}`,
      phoneLabel,
      phoneHref: `tel:${dialable(phoneLabel)}`,
      whatsappLabel,
      whatsappHref: `https://wa.me/${dialable(whatsappLabel).replace('+', '')}`,
      email,
      emailHref: `mailto:${email}`,
      directionsHref: directionsUrl(`${street}, ${zip} ${city}`),
    };
  }, [t]);
};

export default SiteContentProvider;
